import { unwrap, type MemberOut, type RoleOut, type TeamOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Avatar, Button, Icon, Skeleton, Tooltip, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useErrorMessage } from "../lib/api-errors";
import { useApi } from "../session/session-provider";
import { AssignMemberDialog } from "./assign-member-dialog";
import { useScopeLabel } from "./scope-picker";
import { useInvalidateAccess } from "./use-roles";

interface RoleMembersProps {
  role: RoleOut;
  members: readonly MemberOut[] | undefined;
  teams: readonly TeamOut[];
  canManage: boolean;
  totalPermissions: number;
}

function RemoveButton({ role, member }: { role: RoleOut; member: MemberOut }) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const invalidate = useInvalidateAccess();
  const remove = useMutation({
    mutationFn: () =>
      unwrap(
        api.PUT("/v1/members/{membership_id}/roles", {
          params: { path: { membership_id: member.id } },
          body: { assignments: member.assignments.filter((a) => a.role_id !== role.id).map((a) => ({ role_id: a.role_id, scope_type: a.scope_type, scope_ids: a.scope_ids })) },
        }),
      ),
    onSuccess: async () => {
      toast.success(t("roles.assign.removed", { role: role.name, name: member.name }));
      await invalidate();
    },
    onError: (caught) => toast.error(message(caught)),
  });
  const label = `${t("roles.assign.remove")}: ${member.name}`;
  if (member.is_you) {
    return (
      <Tooltip content={t("roles.assign.ownRole")}>
        <span className="inline-flex">
          <Button variant="ghost" size="sm" iconOnly aria-label={label} disabled>
            <Icon name="x" />
          </Button>
        </span>
      </Tooltip>
    );
  }
  return (
    <Button variant="ghost" size="sm" iconOnly aria-label={label} loading={remove.isPending} onClick={() => { remove.mutate(); }}>
      {remove.isPending ? null : <Icon name="x" />}
    </Button>
  );
}

/** Members holding this role (with each assignment's scope) + "Assign to member". */
export function RoleMembers({ role, members, teams, canManage, totalPermissions }: RoleMembersProps) {
  const { t } = useTranslation();
  const scopeLabel = useScopeLabel();
  const [assignOpen, setAssignOpen] = useState(false);
  const holders = (members ?? []).flatMap((m) => m.assignments.filter((a) => a.role_id === role.id).map((a) => ({ member: m, assignment: a })));
  return (
    <section className="card" aria-labelledby="role-members-title">
      <div className="card-header">
        <h2 id="role-members-title" className="card-title">
          {t("roles.assign.title")} <span className="ml-1 font-medium text-fg-subtle">{holders.length}</span>
        </h2>
        {canManage ? (
          <Button variant="secondary" size="sm" onClick={() => { setAssignOpen(true); }}>
            <Icon name="user-plus" />
            {t("roles.assign.add")}
          </Button>
        ) : null}
      </div>
      {members === undefined ? (
        <div className="space-y-2 p-5" aria-hidden="true">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : holders.length === 0 ? (
        <p className="px-5 py-4 text-sm text-fg-muted">{t("roles.assign.empty")}</p>
      ) : (
        <ul className="divide-y divide-line">
          {holders.map(({ member, assignment }) => (
            <li key={`${member.id}-${assignment.scope_type}-${assignment.scope_ids.join(",")}`} className="flex items-center gap-3 px-5 py-2.5">
              <Avatar name={member.name} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold">
                  {member.name} {member.is_you ? <span className="text-xs font-medium text-fg-subtle">{t("roles.assign.you")}</span> : null}
                </span>
                <span className="block truncate text-xs text-fg-subtle">
                  {member.email} · {scopeLabel(assignment)}
                </span>
              </span>
              {canManage ? <RemoveButton role={role} member={member} /> : null}
            </li>
          ))}
        </ul>
      )}
      {canManage && members ? (
        <AssignMemberDialog role={role} members={members} teams={teams} totalPermissions={totalPermissions} open={assignOpen} onOpenChange={setAssignOpen} />
      ) : null}
    </section>
  );
}
